/**
 * Content blocks for one owner. Owner-agnostic on purpose: `Screen.content` and `Activity.content`
 * are the same `ContentBlock[]` and the store's block actions already take an owner, so the very
 * same `ContentBlockList` mounts inside an activity via `{ screenId, activityId }`.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { ContentBlock } from './types'
import type { ContentBlockOwner, ContentBlockType } from './store/interfaces'

import { useAssessmentEditorStore } from './store'

interface ContentBlockItem {
  index: number
  block: ContentBlock
  isFirst: boolean
  isLast: boolean
}

interface ContentBlockListProps {
  owner: ContentBlockOwner
}

interface UseContentBlockListOptions {
  props: ContentBlockListProps
}

interface UseContentBlockListReturn {
  refBlocks: ComputedRef<ContentBlockItem[]>
  refHasBlocks: ComputedRef<boolean>
  addBlock: (type: ContentBlockType) => void
  removeBlock: (index: number) => void
  moveBlockUp: (index: number) => void
  moveBlockDown: (index: number) => void
}

export const useContentBlockList = (
  { props }: UseContentBlockListOptions,
): UseContentBlockListReturn => {
  const store = useAssessmentEditorStore()

  const refContent = computed<ContentBlock[]>(() => {
    const screen = store.getScreens.find(item => item.id === props.owner.screenId)
    if (!screen) {
      return []
    }
    if (!props.owner.activityId) {
      return screen.content
    }
    return screen.activities.find(item => item.id === props.owner.activityId)?.content ?? []
  })

  const refBlocks = computed<ContentBlockItem[]>(() =>
    refContent.value.map((block, index) => ({
      index,
      block,
      isFirst: index === 0,
      isLast: index === refContent.value.length - 1,
    })),
  )

  const refHasBlocks = computed<boolean>(() => refContent.value.length > 0)

  const addBlock = (type: ContentBlockType): void => {
    store.addContentBlock(props.owner, type)
  }

  const removeBlock = (index: number): void => {
    store.removeContentBlock(props.owner, index)
  }

  const moveBlockUp = (index: number): void => {
    store.moveContentBlockUp(props.owner, index)
  }

  const moveBlockDown = (index: number): void => {
    store.moveContentBlockDown(props.owner, index)
  }

  return {
    refBlocks,
    refHasBlocks,
    addBlock,
    removeBlock,
    moveBlockUp,
    moveBlockDown,
  }
}
